import clsx from 'clsx';
import { X } from 'lucide-react';

/**
 * Badge Component
 * Status indicators and labels
 * Variants: primary, secondary, success, warning, danger, muted
 * Sizes: sm, md, lg
 * Optional close button (for tags)
 */

function Badge({
  children,
  variant = 'primary',
  size = 'md',
  onRemove,
  className = '',
  ...props
}) {
  const baseClasses = 'inline-flex items-center gap-1.5 font-medium rounded-full transition-all duration-200';

  const variantClasses = {
    primary: 'bg-gradient-to-r from-primary-100 to-primary-50 text-primary-700 border border-primary-200',
    secondary: 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 border border-purple-200',
    success: 'bg-gradient-to-r from-success-100 to-success-50 text-success-700 border border-success-200',
    warning: 'bg-gradient-to-r from-warning-100 to-warning-50 text-warning-700 border border-warning-200',
    danger: 'bg-gradient-to-r from-danger-100 to-danger-50 text-danger-700 border border-danger-200',
    muted: 'bg-slate-100 text-slate-700 border border-slate-200',
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-xs',
    lg: 'px-4 py-1.5 text-sm',
  };

  return (
    <span
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {children}
      {onRemove && (
        <button
          onClick={onRemove}
          className="hover:opacity-70 transition-opacity"
          aria-label="Remove badge"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

/**
 * StatusBadge Component
 * Pre-configured badge for common statuses
 */
export function StatusBadge({ status }) {
  const statusConfig = {
    // Order statuses
    pending: { label: 'Pending', variant: 'warning' },
    assigned: { label: 'Assigned', variant: 'primary' },
    'in-progress': { label: 'In Progress', variant: 'primary' },
    completed: { label: 'Completed', variant: 'success' },
    cancelled: { label: 'Cancelled', variant: 'danger' },
    refunded: { label: 'Refunded', variant: 'muted' },

    // User/Driver statuses
    active: { label: 'Active', variant: 'success' },
    inactive: { label: 'Inactive', variant: 'muted' },
    suspended: { label: 'Suspended', variant: 'danger' },
    blocked: { label: 'Blocked', variant: 'danger' },

    // Payment statuses
    paid: { label: 'Paid', variant: 'success' },
    unpaid: { label: 'Unpaid', variant: 'warning' },
    failed: { label: 'Failed', variant: 'danger' },

    // Delete request statuses
    reviewed: { label: 'Reviewed', variant: 'primary' },
    approved: { label: 'Approved', variant: 'success' },
    rejected: { label: 'Rejected', variant: 'danger' },

    // Online status
    online: { label: 'Online', variant: 'success' },
    offline: { label: 'Offline', variant: 'muted' },
  };

  const config = statusConfig[status] || { label: status, variant: 'muted' };

  return (
    <Badge variant={config.variant}>
      {config.label}
    </Badge>
  );
}

/**
 * DotBadge Component
 * Minimalist status indicator with just a colored dot
 */
export function DotBadge({ variant = 'primary', label }) {
  const dotColors = {
    primary: 'bg-primary-500',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    danger: 'bg-danger-500',
    muted: 'bg-slate-400',
  };

  return (
    <span className="inline-flex items-center gap-2">
      <span className={clsx('w-2 h-2 rounded-full', dotColors[variant])} aria-hidden="true" />
      {label && <span className="text-sm text-slate-700">{label}</span>}
    </span>
  );
}

// Export Badge as both named and default
export { Badge };
export default Badge;

